/**
 * TimeClock — PUBLIC page at /timeclock (crew bookmark, no CRM login).
 * Employee picks name + PIN → big Clock In/Out with GPS capture, week summary,
 * expense entry with receipt photo, mileage, and Submit Week.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Clock, LogIn, LogOut, ReceiptText, Send, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const F = "w-full h-11 px-3 border border-zinc-300 bg-white rounded-sm text-sm";
const L = "block text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-700 mb-1";
const CATS = ["Fuel", "Materials", "Meals", "Lodging", "Mileage", "Other"];

const getGps = () => new Promise((res) => {
  if (!navigator.geolocation) return res(null);
  navigator.geolocation.getCurrentPosition(
    (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
    () => res(null), { timeout: 8000 }
  );
});

export default function TimeClock() {
  const [boot, setBoot] = useState({ employees: [], projects: [] });
  const [empId, setEmpId] = useState(localStorage.getItem("tc_emp") || "");
  const [pin, setPin] = useState("");
  const [st, setSt] = useState(null); // status payload after auth
  const [projectId, setProjectId] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [exp, setExp] = useState({ category: "Fuel", amount: "", miles: "", description: "", receipt_b64: "" });
  const [off, setOff] = useState({ type: "Vacation", start_date: "", end_date: "", note: "" });

  useEffect(() => { axios.get(`${API}/public/timeclock/bootstrap`).then((r) => setBoot(r.data)).catch(() => {}); }, []);

  const creds = () => ({ employee_id: empId, pin });
  const refresh = async () => {
    const r = await axios.post(`${API}/public/timeclock/status`, creds());
    setSt(r.data);
  };
  const login = async () => {
    setErr(""); setBusy("login");
    try { await refresh(); localStorage.setItem("tc_emp", empId); }
    catch (e) { setErr(e?.response?.data?.detail || "Login failed"); }
    finally { setBusy(""); }
  };

  const doPunch = async (action) => {
    setErr(""); setBusy(action);
    try {
      const gps = await getGps();
      const proj = boot.projects.find((p) => p.id === projectId);
      await axios.post(`${API}/public/timeclock/punch`, {
        ...creds(), action, project_id: projectId,
        project_title: proj?.title || "Shop / Overhead",
        lat: gps?.lat, lng: gps?.lng,
      });
      await refresh();
    } catch (e) { setErr(e?.response?.data?.detail || "Punch failed"); }
    finally { setBusy(""); }
  };

  const onReceipt = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      const s = Math.min(1, 1200 / Math.max(img.width, img.height));
      c.width = img.width * s; c.height = img.height * s;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      setExp((p) => ({ ...p, receipt_b64: c.toDataURL("image/jpeg", 0.7) }));
    };
    img.src = URL.createObjectURL(f);
  };

  const addExpense = async () => {
    setErr(""); setBusy("expense");
    try {
      const proj = boot.projects.find((p) => p.id === projectId);
      await axios.post(`${API}/public/timeclock/expense`, {
        ...creds(), ...exp, amount: Number(exp.amount) || 0, miles: Number(exp.miles) || 0,
        project_id: projectId, project_title: proj?.title || "Shop / Overhead",
      });
      setExp({ category: "Fuel", amount: "", miles: "", description: "", receipt_b64: "" });
      await refresh();
    } catch (e) { setErr(e?.response?.data?.detail || "Expense failed"); }
    finally { setBusy(""); }
  };

  const requestOff = async () => {
    setErr(""); setBusy("off");
    try {
      await axios.post(`${API}/public/timeclock/timeoff`, { ...creds(), ...off, end_date: off.end_date || off.start_date });
      setOff({ type: "Vacation", start_date: "", end_date: "", note: "" });
      await refresh();
    } catch (e) { setErr(e?.response?.data?.detail || "Request failed"); }
    finally { setBusy(""); }
  };

  const submitWeek = async () => {
    setBusy("submit");
    try { await axios.post(`${API}/public/timeclock/submit-week`, creds()); await refresh(); }
    catch (e) { setErr(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(""); }
  };

  const open = st?.open_punch;
  const weekHours = (st?.week_entries || []).reduce((s, e) => s + Number(e.hours || 0), 0);

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center px-4 py-8" data-testid="timeclock-page">
      <img src="/sealtech-logo.png" alt="SealTech" className="h-12 w-auto mb-4 bg-white rounded-sm p-1.5" />
      <div className="w-full max-w-md bg-white rounded-sm shadow-2xl overflow-hidden">
        <div className="px-6 py-4 bg-blue-700 text-white flex items-center gap-2">
          <Clock className="w-5 h-5" />
          <div>
            <h1 className="font-heading text-xl font-black tracking-tight">SealTech Time Clock</h1>
            <p className="text-[11px] text-blue-100">Clock in, log expenses, submit your week.</p>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {err && <div className="text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-sm p-2" data-testid="tc-error">{err}</div>}
          {!st ? (
            <>
              <div>
                <label className={L}>Who are you?</label>
                <select data-testid="tc-employee" className={F} value={empId} onChange={(e) => setEmpId(e.target.value)}>
                  <option value="">Select your name…</option>
                  {boot.employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div>
                <label className={L}>PIN</label>
                <input data-testid="tc-pin" type="password" inputMode="numeric" className={F} value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••" />
              </div>
              <button data-testid="tc-login" disabled={!empId || !pin || busy === "login"} onClick={login} className="w-full h-12 bg-zinc-950 text-white text-sm font-bold uppercase tracking-wider rounded-sm disabled:opacity-50">
                {busy === "login" ? "Checking…" : "Continue"}
              </button>
            </>
          ) : (
            <>
              {open ? (
                <div className="border-2 border-emerald-500 bg-emerald-50 rounded-sm p-3 text-sm" data-testid="tc-open-punch">
                  <div className="font-bold text-emerald-800">Clocked in — {open.project_title}</div>
                  <div className="text-xs text-emerald-700">since {new Date(open.clock_in).toLocaleTimeString()} {open.gps_missing && "· ⚠ no location"}</div>
                </div>
              ) : (
                <div>
                  <label className={L}>Project</label>
                  <select data-testid="tc-project" className={F} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                    {boot.projects.map((p) => <option key={p.id || "shop"} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
              )}
              <button
                data-testid="tc-punch"
                disabled={!!busy}
                onClick={() => doPunch(open ? "out" : "in")}
                className={`w-full h-16 text-white text-lg font-black uppercase tracking-wider rounded-sm disabled:opacity-50 inline-flex items-center justify-center gap-2 ${open ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
              >
                {open ? <LogOut className="w-6 h-6" /> : <LogIn className="w-6 h-6" />}
                {busy === "in" || busy === "out" ? "Working…" : open ? "Clock Out" : "Clock In"}
              </button>
              <div className="text-[10px] text-zinc-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Location is stamped on each punch when your phone allows it.</div>

              <div className="border-t border-zinc-200 pt-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">This week · {weekHours.toFixed(2)} hrs</div>
                <div className="space-y-1 max-h-36 overflow-y-auto" data-testid="tc-week">
                  {(st.week_entries || []).map((e) => (
                    <div key={e.id} className="flex justify-between text-xs text-zinc-700">
                      <span>{e.clock_in.slice(5, 10)} · {e.project_title}</span>
                      <span className="font-mono">{e.status === "open" ? "…" : `${Number(e.hours).toFixed(2)}h`} <span className={`ml-1 text-[9px] uppercase font-bold ${e.status === "approved" ? "text-emerald-600" : e.status === "rejected" ? "text-red-600" : "text-zinc-400"}`}>{e.status}</span></span>
                    </div>
                  ))}
                  {(st.week_expenses || []).map((x) => (
                    <div key={x.id} className="flex justify-between text-xs text-zinc-500">
                      <span>{x.expense_date.slice(5)} · {x.category}</span>
                      <span className="font-mono">${Number(x.amount).toFixed(2)} <span className="ml-1 text-[9px] uppercase font-bold text-zinc-400">{x.status}</span></span>
                    </div>
                  ))}
                </div>
              </div>

              <details className="border border-zinc-200 rounded-sm p-3">
                <summary className="text-xs font-bold uppercase tracking-wider text-zinc-700 cursor-pointer inline-flex items-center gap-1"><ReceiptText className="w-3.5 h-3.5" /> Add Expense</summary>
                <div className="space-y-2 mt-3">
                  <select data-testid="tc-exp-cat" className={F} value={exp.category} onChange={(e) => setExp({ ...exp, category: e.target.value })}>
                    {CATS.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  {exp.category === "Mileage" ? (
                    <input data-testid="tc-exp-miles" type="number" className={F} value={exp.miles} onChange={(e) => setExp({ ...exp, miles: e.target.value })} placeholder="Miles driven" />
                  ) : (
                    <>
                      <input data-testid="tc-exp-amount" type="number" step="0.01" className={F} value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} placeholder="Amount ($)" />
                      <div>
                        <label className={L}>Receipt photo (required)</label>
                        <input data-testid="tc-exp-receipt" type="file" accept="image/*" capture="environment" onChange={onReceipt} className="text-xs" />
                        {exp.receipt_b64 && <span className="text-[10px] text-emerald-700 font-bold">✓ attached</span>}
                      </div>
                    </>
                  )}
                  <input data-testid="tc-exp-desc" className={F} value={exp.description} onChange={(e) => setExp({ ...exp, description: e.target.value })} placeholder="What was it for?" />
                  <button data-testid="tc-exp-save" disabled={busy === "expense"} onClick={addExpense} className="w-full h-10 bg-zinc-950 text-white text-xs font-bold uppercase tracking-wider rounded-sm disabled:opacity-50">Save Expense</button>
                </div>
              </details>

              {st.balances && (
                <div className="grid grid-cols-2 gap-2 text-center" data-testid="tc-balances">
                  <div className="border border-zinc-200 rounded-sm p-2">
                    <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Vacation Left</div>
                    <div className="text-lg font-black">{st.balances.vacation_left}h</div>
                  </div>
                  <div className="border border-zinc-200 rounded-sm p-2">
                    <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Sick Available</div>
                    <div className="text-lg font-black">{st.balances.sick_left}h</div>
                  </div>
                </div>
              )}
              <details className="border border-zinc-200 rounded-sm p-3">
                <summary className="text-xs font-bold uppercase tracking-wider text-zinc-700 cursor-pointer">Request Time Off</summary>
                <div className="space-y-2 mt-3">
                  <select data-testid="tc-off-type" className={F} value={off.type} onChange={(e) => setOff({ ...off, type: e.target.value })}>
                    {["Vacation", "Sick", "Holiday", "Unpaid"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="tc-off-start" type="date" className={F} value={off.start_date} onChange={(e) => setOff({ ...off, start_date: e.target.value })} />
                    <input data-testid="tc-off-end" type="date" className={F} value={off.end_date} onChange={(e) => setOff({ ...off, end_date: e.target.value })} />
                  </div>
                  <input data-testid="tc-off-note" className={F} value={off.note} onChange={(e) => setOff({ ...off, note: e.target.value })} placeholder="Note (optional)" />
                  <button data-testid="tc-off-save" disabled={!off.start_date || busy === "off"} onClick={requestOff} className="w-full h-10 bg-zinc-950 text-white text-xs font-bold uppercase tracking-wider rounded-sm disabled:opacity-50">Send Request</button>
                  <div className="space-y-1">
                    {(st.time_off || []).slice(0, 4).map((o) => (
                      <div key={o.id} className="flex justify-between text-[11px] text-zinc-600">
                        <span>{o.type} {o.start_date.slice(5)}{o.end_date !== o.start_date ? `→${o.end_date.slice(5)}` : ""}</span>
                        <span className={`font-bold uppercase text-[9px] ${o.status === "approved" ? "text-emerald-600" : o.status === "rejected" ? "text-red-600" : "text-zinc-400"}`}>{o.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </details>

              <button data-testid="tc-submit-week" disabled={busy === "submit"} onClick={submitWeek} className="w-full h-11 border-2 border-blue-700 text-blue-700 text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-blue-50 inline-flex items-center justify-center gap-1">
                <Send className="w-3.5 h-3.5" /> Submit Week for Approval
              </button>
            </>
          )}
        </div>
      </div>
      <div className="text-[11px] text-zinc-500 mt-4">© {new Date().getFullYear()} SealTech Building Solutions</div>
    </div>
  );
}
